import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

const sections = [
  {
    id: 'overview',
    title: '1. Our Role Under GDPR',
    content: `Uni5d Technology Solutions LLC operates the Quali5d platform and processes personal data in two capacities:

Data Processor: When your organisation (the Data Controller) uses Quali5d to process personal data of your employees, hiring managers, and job candidates, Uni5d acts as a Data Processor under Article 28 of the GDPR.

Data Controller: Uni5d acts as a Data Controller for the personal data of our own customers (company administrators and users) collected during account registration and platform use.

Lawful Basis for Processing:
• Contract performance — processing necessary to deliver the subscribed services
• Legitimate interests — platform security, fraud prevention, product improvement
• Legal obligation — retaining billing records as required by law
• Consent — where explicitly obtained for specific processing activities`
  },
  {
    id: 'rights',
    title: '2. EU/EEA Data Subject Rights',
    content: `Under GDPR, individuals whose data we process have the following rights:

Right of Access (Article 15)
Request a copy of the personal data we hold about you, the purposes of processing, and the categories of data involved.

Right to Rectification (Article 16)
Request correction of inaccurate or incomplete personal data.

Right to Erasure / "Right to Be Forgotten" (Article 17)
Request deletion of your personal data where there is no legitimate reason for continued processing. Note: we may retain certain data where required by legal obligation (e.g. billing records for 7 years).

Right to Restriction of Processing (Article 18)
Request that we restrict processing of your personal data in certain circumstances.

Right to Data Portability (Article 20)
Receive your personal data in a structured, commonly used, machine-readable format and transmit it to another controller.

Right to Object (Article 21)
Object to processing based on legitimate interests or for direct marketing purposes.

Rights Related to Automated Decision-Making (Article 22)
Quali5d uses AI to generate candidate scores and assessments. These outputs are advisory tools to assist human decision-makers — they are not binding automated decisions. However, you have the right to:
• Be informed about automated processing
• Request human review of any AI-generated assessment
• Contest any AI-generated output

How to Exercise Your Rights:
Email: admin@uni5d.ai
Subject: "GDPR Data Subject Request"
Response time: Within 30 days of receipt`
  },
  {
    id: 'transfers',
    title: '3. International Data Transfers',
    content: `Uni5d Technology Solutions LLC is based in the United States. When EU/EEA personal data is processed using Quali5d, it is transferred to and processed in the United States.

Legal Mechanism for Transfers:
We rely on Standard Contractual Clauses (SCCs) as approved by the European Commission (Decision 2021/914) as the lawful transfer mechanism for EU/EEA personal data transferred to the United States.

Sub-Processor Transfers:
All our sub-processors are US-based. We ensure each sub-processor has appropriate safeguards in place:

• Microsoft Azure — EU SCCs, Azure Data Protection Addendum
• Anthropic, Inc. — Data Processing Agreement with SCCs
• ElevenLabs, Inc. — Data Processing Agreement
• Stripe, Inc. — EU SCCs, Stripe Data Processing Agreement

Copies of applicable SCCs and DPAs are available upon request at admin@uni5d.ai.`
  },
  {
    id: 'subprocessors',
    title: '4. Sub-Processors',
    content: `We use the following sub-processors to deliver our services. All sub-processors are contractually bound to process data only as instructed and to maintain appropriate security measures.

Sub-Processor | Service | Location | Safeguard
─────────────────────────────────────────────────────
Microsoft Azure | Cloud infrastructure, storage, speech services | United States | SCCs + Azure DPA
Anthropic, Inc. | AI document generation and candidate scoring | United States | DPA
ElevenLabs, Inc. | AI voice conversations (intake calls, interviews) | United States | DPA
Stripe, Inc. | Payment processing and billing | United States | SCCs + Stripe DPA

We will notify customers of any intended changes to sub-processors (additions or replacements) with at least 14 days notice, giving customers the opportunity to object.

To request the full list of sub-processors with their current DPAs, contact admin@uni5d.ai.`
  },
  {
    id: 'dpa',
    title: '5. Data Processing Agreement (DPA)',
    content: `For customers who are subject to GDPR and process EU/EEA personal data using Quali5d, a Data Processing Agreement (DPA) is required under Article 28 GDPR.

Our DPA covers:
• Subject matter and duration of processing
• Nature and purpose of processing
• Type of personal data processed
• Categories of data subjects
• Obligations and rights of the controller
• Sub-processor arrangements
• Security measures
• Deletion and return of data
• Audit rights

How to obtain a DPA:
Email admin@uni5d.ai with the subject "DPA Request" and your company name. We will respond with the DPA within 3 business days.

The DPA is incorporated by reference into the Quali5d Terms of Service upon execution.`
  },
  {
    id: 'retention',
    title: '6. Data Retention Under GDPR',
    content: `We apply the GDPR data minimisation and storage limitation principles to all personal data:

Audio & Video Recordings: 90 days from upload/session date, then permanently deleted. This includes interview recordings of candidates and intake call recordings of hiring managers.

Transcripts & AI-Generated Documents: Retained for the duration of the subscription plus 30 days. Deleted within 30 days of account termination.

Account & Contact Data: Retained for the duration of the subscription plus 90 days.

Billing & Transaction Records: 7 years as required by US federal tax law (IRC § 6001) and applicable state law.

Right to Request Early Deletion:
Data subjects may request deletion of their personal data prior to the standard retention period by contacting admin@uni5d.ai. We will process valid deletion requests within 30 days, subject to legal retention obligations.`
  },
  {
    id: 'complaints',
    title: '7. Supervisory Authority & Complaints',
    content: `If you are located in the EU/EEA and believe we have not handled your personal data in compliance with GDPR, you have the right to lodge a complaint with your local supervisory authority.

Find your supervisory authority:
https://edpb.europa.eu/about-edpb/about-edpb/members_en

We encourage you to contact us first at admin@uni5d.ai so we can address your concern directly. We aim to resolve all privacy complaints within 15 business days.

For UK residents: You may also contact the Information Commissioner's Office (ICO) at ico.org.uk.`
  }
];

export default function GDPRPage() {
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState('overview');

  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div className="min-h-screen bg-gray-50">
      <Helmet>
        <title>GDPR Compliance — Quali5d Data Protection for EU/EEA Customers</title>
        <meta name="description" content="How Quali5d complies with GDPR. Standard Contractual Clauses, sub-processor list, Data Processing Agreement available on request, 72-hour breach notification." />
        <link rel="canonical" href="https://www.quali5d.com/gdpr" />
        <meta property="og:title" content="GDPR Compliance — Quali5d Data Protection for EU/EEA Customers" />
        <meta property="og:description" content="How Quali5d complies with GDPR. Standard Contractual Clauses, sub-processor list, Data Processing Agreement available on request, 72-hour breach notification." />
        <meta property="og:url" content="https://www.quali5d.com/gdpr" />
        <meta name="twitter:title" content="GDPR Compliance — Quali5d Data Protection for EU/EEA Customers" />
        <meta name="twitter:description" content="How Quali5d complies with GDPR. Standard Contractual Clauses, sub-processor list, Data Processing Agreement available on request, 72-hour breach notification." />
      </Helmet>
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <button onClick={() => navigate('/')} className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M10 19l-7-7m0 0l7-7m-7 7h18"/>
            </svg>
            <span className="text-sm font-medium">Back to Quali5d</span>
          </button>
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 bg-blue-600 rounded-lg flex items-center justify-center">
              <span className="text-white font-black text-xs">Q5</span>
            </div>
            <span className="font-bold text-gray-900 text-sm">Quali5d</span>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-12">
        {/* Hero */}
        <div className="mb-10">
          <div className="inline-flex items-center gap-2 bg-blue-50 border border-blue-200 rounded-full px-4 py-1.5 mb-4">
            <span className="text-blue-700 text-sm">🇪🇺</span>
            <span className="text-xs font-semibold text-blue-700">GDPR Compliance</span>
          </div>
          <h1 className="text-4xl font-black text-gray-900 mb-3">GDPR & Data Protection</h1>
          <p className="text-gray-500 text-lg">How Quali5d complies with the General Data Protection Regulation for EU/EEA customers.</p>
          <div className="flex flex-wrap gap-4 mt-4 text-sm text-gray-500">
            <span className="flex items-center gap-1.5"><span className="w-2 h-2 bg-blue-500 rounded-full"></span>Standard Contractual Clauses in place</span>
            <span className="flex items-center gap-1.5"><span className="w-2 h-2 bg-green-500 rounded-full"></span>DPA available on request</span>
            <span className="flex items-center gap-1.5"><span className="w-2 h-2 bg-purple-500 rounded-full"></span>72-hour breach notification</span>
          </div>
        </div>

        <div className="flex gap-8">
          {/* Sidebar */}
          <div className="hidden lg:block w-64 flex-shrink-0">
            <div className="sticky top-24 bg-white rounded-2xl border border-gray-200 p-4 max-h-[calc(100vh-120px)] overflow-y-auto">
              <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3 px-2">Contents</p>
              <nav className="space-y-0.5">
                {sections.map(s => (
                  <a key={s.id} href={`#${s.id}`}
                    onClick={() => setActiveSection(s.id)}
                    className={`block px-3 py-2 rounded-lg text-sm transition-colors ${
                      activeSection === s.id ? 'bg-blue-50 text-blue-700 font-semibold' : 'text-gray-600 hover:bg-gray-50'
                    }`}>
                    {s.title}
                  </a>
                ))}
              </nav>
              <div className="mt-4 pt-4 border-t border-gray-100">
                <a href="mailto:admin@uni5d.ai?subject=DPA Request"
                  className="block w-full text-center bg-blue-600 text-white text-xs font-bold py-2.5 rounded-xl hover:bg-blue-700 transition-colors">
                  Request DPA →
                </a>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0">
            <div className="space-y-6">
              {sections.map(s => (
                <div key={s.id} id={s.id}
                  className="bg-white rounded-2xl border border-gray-200 p-8"
                  onMouseEnter={() => setActiveSection(s.id)}>
                  <h2 className="text-xl font-bold text-gray-900 mb-4 pb-3 border-b border-gray-100">{s.title}</h2>
                  <div className="text-gray-600 text-sm leading-relaxed whitespace-pre-line">{s.content}</div>
                </div>
              ))}
            </div>

            <div className="mt-8 bg-blue-50 border border-blue-200 rounded-2xl p-6">
              <p className="text-sm font-semibold text-blue-800 mb-1">Need a Data Processing Agreement?</p>
              <p className="text-sm text-blue-700">Email <a href="mailto:admin@uni5d.ai?subject=DPA Request" className="font-bold underline">admin@uni5d.ai</a> with subject "DPA Request" — we'll respond within 3 business days.</p>
            </div>

            <div className="mt-6 text-center text-sm text-gray-400">
              © 2026 Uni5d Technology Solutions LLC · EIN: 42-1896513 · Mooresville, North Carolina
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}